<template>
  <div class="hubspot-card">
    <div class="hubspot-card__content">
      <transition name="fade">
        <div v-if="showFallback" class="hubspot-card__fallback">
          <img
            :alt="imgAlt || 'Logo'"
            loading="lazy"
            fetchpriority="high"
            :src="optimizedSrc"
            :srcset="srcSet"
            sizes="(max-width: 1100px) 100vw, 1100px"
            @error="handleImageError"
          />
        </div>
      </transition>

      <div
        ref="hubspotContainer"
        class="hubspot-card__embed"
        :class="[
          `hs-cta-embed hs-cta-simple-placeholder hs-cta-embed-${hubspotId}`,
          { 'hubspot-card__embed--visible': !showFallback },
        ]"
        :data-hubspot-wrapper-cta-id="hubspotId"
        :aria-busy="isLoading"
      ></div>
    </div>

    <transition name="fade">
      <div
        v-if="isLoading && !showFallback"
        class="hubspot-card__overlay"
        data-test="hubspot-card-loader"
      >
        <ActionLoader :size="1.4" loaderClass="hubspot-card__spinner" />
      </div>
    </transition>

    <div v-if="hasError" class="hubspot-card__error" data-test="hubspot-card-error">
      <p class="hubspot-card__error-text">
        {{ errorMessage || 'Unable to load this content right now.' }}
      </p>
      <button type="button" class="hubspot-card__retry" @click="reload">Retry</button>
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import { useHubspotEmbed } from '@/composables/useHubspotEmbed'

export default {
  name: 'HubspotCard',
  props: {
    hubspotId: {
      type: String,
      required: true,
    },
    imgSrc: {
      type: String,
      required: true,
    },
    imgAlt: {
      type: String,
      default: 'Logo',
    },
  },
  setup(props) {
    const hubspotContainer = ref(null)
    const { isLoading, hasError, errorMessage, showFallback, reload } = useHubspotEmbed(
      hubspotContainer,
      {
        maxRetries: 3,
      }
    )

    watch(
      () => [props.hubspotId, props.imgSrc],
      () => {
        reload()
      }
    )

    return {
      hubspotContainer,
      isLoading,
      hasError,
      errorMessage,
      showFallback,
      reload,
    }
  },
  computed: {
    optimizedSrc() {
      return this.buildOptimizedUrl(this.imgSrc, { width: 1400, format: 'webp' })
    },
    srcSet() {
      const widths = [480, 960, 1400]
      const set = widths
        .map(width => `${this.buildOptimizedUrl(this.imgSrc, { width, format: 'webp' })} ${width}w`)
        .join(', ')
      return set || this.imgSrc
    },
  },
  methods: {
    handleImageError(event) {
      event.target.style.display = 'none'
    },
    buildOptimizedUrl(src, params) {
      try {
        const url = new URL(src)
        Object.entries(params).forEach(([key, value]) => {
          url.searchParams.set(key, value)
        })
        return url.toString()
      } catch (e) {
        return src
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.hubspot-card {
  position: relative;
  width: 100%;
  min-height: 559px;
  display: flex;
  align-items: center;
  justify-content: center;

  &__content {
    display: grid;
    width: 100%;

    > * {
      grid-area: 1 / 1;
    }
  }

  &__embed {
    width: 100%;
    opacity: 0;
    transition: opacity 0.3s ease;

    &--visible {
      opacity: 1;
    }
  }

  &__fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: start;
    z-index: 1;

    img {
      display: block;
      width: auto;
      transform: scale(1.27);
    }
  }

  &__overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.88);
    text-align: center;
    z-index: 2;
  }

  &__error {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid #f5c6cb;
    border-radius: 4px;
  }

  &__error-text {
    color: #8b1a1a;
    margin-bottom: 0.75rem;
  }

  &__retry {
    border: none;
    background: #ef7d22;
    color: #fff;
    padding: 0.5rem 1.25rem;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 600;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
